import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CreatePriceApprovalRangeDto, PriceApprovalRange } from '../../../models/PriceApprovalRange';
import { StaffMember } from '../../../models/StaffMember';
import { PriceApprovalRangesService } from '../../../services/price-approval-ranges.service';
import { StaffService } from '../../../services/staff.service';
import { ErrorService } from '../../../shared/services/error.service';

@Component({
  selector: 'app-price-approval-range-form',
  templateUrl: './price-approval-range-form.component.html',
  styleUrls: ['./price-approval-range-form.component.css']
})
export class PriceApprovalRangeFormComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  form: FormGroup;
  staffMembers: StaffMember[] = [];
  loadingStaff = false;
  loadingRange = false;
  isSaving = false;
  ruleId: number | null = null;
  mode: 'create' | 'edit' | 'view' = 'create';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private priceApprovalRangesService: PriceApprovalRangesService,
    private staffService: StaffService,
    private router: Router,
    private errorService: ErrorService
  ) {
    this.form = this.fb.group({
      ruleName: ['', Validators.required],
      minAmount: [null as number | null, [Validators.required, Validators.min(0)]],
      maxAmount: [null as number | null, [Validators.required, Validators.min(0)]],
      requiresManagerApproval: [true],
      skipApprovalWhenInRange: [false],
      requiresSequentialApproval: [true],
      isActive: [true],
      description: [''],
      additionalApprovers: this.fb.array([this.createApproverGroup(), this.createApproverGroup()])
    });
  }

  ngOnInit(): void {
    this.resolveRouteMode();
    this.loadStaff();
    if (this.ruleId) {
      this.loadRange(this.ruleId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get approvers(): FormArray {
    return this.form.get('additionalApprovers') as FormArray;
  }

  private createApproverGroup(): FormGroup {
    return this.fb.group({
      approvalLevel: ['Level1', Validators.required],
      staffId: [null as number | null, Validators.required]
    });
  }

  addApprover(): void {
    if (this.approvers.length >= 5) {
      this.errorService.showInfoMessage('Limite de 5 níveis de aprovação atingido.');
      return;
    }
    const g = this.createApproverGroup();
    const nextLevel = this.approvers.length + 1;
    g.patchValue({ approvalLevel: `Level${nextLevel}` });
    this.approvers.push(g);
  }

  removeApprover(index: number): void {
    if (this.approvers.length <= 1) return;
    this.approvers.removeAt(index);
  }

  private loadStaff(): void {
    this.loadingStaff = true;
    this.staffService
      .getStaffPaginated({ page: 1, pageSize: 500 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.staffMembers = (res.staff || []).filter((s) => !s.Inactive && !s.Blocked);
          this.loadingStaff = false;
        },
        error: () => {
          this.loadingStaff = false;
          this.errorService.showErrorMessage('Não foi possível carregar colaboradores para os aprovadores.');
        }
      });
  }

  cancel(): void {
    this.router.navigate(['/price-approval/ranges']);
  }

  submit(): void {
    if (this.mode === 'view') {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorService.showErrorMessage('Preencha os campos obrigatórios e os aprovadores.');
      return;
    }

    const v = this.form.getRawValue();
    const min = Number(v.minAmount);
    const max = Number(v.maxAmount);
    if (max < min) {
      this.errorService.showErrorMessage('O valor máximo deve ser maior ou igual ao mínimo.');
      return;
    }

    const approversPayload = (v.additionalApprovers as { approvalLevel: string; staffId: number | null }[])
      .map((row, i) => {
        const sid = Number(row.staffId);
        if (!Number.isFinite(sid) || sid <= 0) return null;
        return {
          approvalLevel: (row.approvalLevel || `Level${i + 1}`).trim(),
          staffId: sid,
          responsibleStaffId: sid,
          approvalOrder: i + 1,
          isRequired: true,
          isActive: true
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    if (approversPayload.length > 5) {
      this.errorService.showErrorMessage('No máximo 5 níveis de aprovação adicionais são permitidos.');
      return;
    }

    const payload: CreatePriceApprovalRangeDto = {
      ruleName: String(v.ruleName || '').trim(),
      minAmount: min,
      maxAmount: max,
      requiresManagerApproval: !!v.requiresManagerApproval,
      requiredAdditionalApprovalsCount: approversPayload.length,
      skipApprovalWhenInRange: !!v.skipApprovalWhenInRange,
      requiresSequentialApproval: !!v.requiresSequentialApproval,
      isActive: !!v.isActive,
      description: String(v.description || '').trim() || undefined,
      additionalApprovers: approversPayload
    };

    this.isSaving = true;
    const req$ = this.ruleId
      ? this.priceApprovalRangesService.update(this.ruleId, payload)
      : this.priceApprovalRangesService.create(payload);

    req$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.errorService.showSuccessMessage(
            this.ruleId ? 'Faixa de aprovação atualizada com sucesso.' : 'Faixa de aprovação criada com sucesso.'
          );
          this.router.navigate(['/price-approval/ranges']);
        },
        error: () => {
          this.isSaving = false;
        }
      });
  }

  private resolveRouteMode(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (Number.isFinite(id) && id > 0) {
      this.ruleId = id;
    }
    const modeFromRoute = this.route.snapshot.data['mode'] as 'view' | 'edit' | undefined;
    this.mode = modeFromRoute ?? (this.ruleId ? 'edit' : 'create');
    if (this.mode === 'view') {
      this.form.disable({ emitEvent: false });
    }
  }

  private loadRange(ruleId: number): void {
    this.loadingRange = true;
    this.priceApprovalRangesService
      .getById(ruleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (range) => {
          this.patchForm(range);
          this.loadingRange = false;
          if (this.mode === 'view') {
            this.form.disable({ emitEvent: false });
          }
        },
        error: () => {
          this.loadingRange = false;
          this.router.navigate(['/price-approval/ranges']);
        }
      });
  }

  private patchForm(range: PriceApprovalRange): void {
    this.form.patchValue({
      ruleName: range.ruleName || '',
      minAmount: Number(range.minAmount ?? 0),
      maxAmount: Number(range.maxAmount ?? 0),
      requiresManagerApproval: !!range.requiresManagerApproval,
      skipApprovalWhenInRange: !!range.skipApprovalWhenInRange,
      requiresSequentialApproval: !!range.requiresSequentialApproval,
      isActive: !!range.isActive,
      description: range.description || ''
    });

    const mapped = (range.additionalApprovers || []).map((a, i) =>
      this.fb.group({
        approvalLevel: [a.approvalLevel || `Level${i + 1}`, Validators.required],
        staffId: [a.staffId ?? null, Validators.required]
      })
    );
    const arr = this.fb.array(mapped.length ? mapped : [this.createApproverGroup()]);
    this.form.setControl('additionalApprovers', arr);
  }
}
